import React, { useEffect, useRef, useState } from 'react';
import { Editor, getMarkRange } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { Dialog } from '../dialogs/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Api } from '../../general/api';
import { ProtectedFileSelect } from './protected-file-select';

interface LinkDraft {
    from: number;
    to: number;
    href: string;
    title: string;
    text: string;
    originalText: string;
    existing: boolean;
    source: 'url' | 'protected';
    filePath: string;
}

function readLink(editor: Editor, position?: number, api?: Api | null): LinkDraft | null {
    const { selection, doc, schema } = editor.state;
    if (!schema.marks.link) return null;
    if (position === undefined && (!selection.$from.sameParent(selection.$to) || !selection.$from.parent.isTextblock
        || !(selection instanceof TextSelection))) return null;
    const $pos = position === undefined ? selection.$from : doc.resolve(position);
    const linkMark = ($pos.nodeAfter?.marks ?? []).find(mark => mark.type === schema.marks.link)
        ?? ($pos.nodeBefore?.marks ?? []).find(mark => mark.type === schema.marks.link);
    const candidate = linkMark && getMarkRange($pos, schema.marks.link, linkMark.attrs);
    const range = candidate && (position !== undefined || selection.to <= candidate.to) ? candidate : undefined;
    const from = range?.from ?? selection.from;
    const to = range?.to ?? selection.to;
    let href = '';
    let title = '';
    doc.nodesBetween(from, to, node => {
        const mark = node.marks.find(mark => mark.type === schema.marks.link);
        if (mark) { href = mark.attrs.href; title = mark.attrs.title ?? ''; }
    });
    const text = doc.textBetween(from, to, '');
    const filePath = href && api?.getProtectedStorageFilePath(href);
    return { from, to, href, title, text, originalText: text, existing: !!range,
        source: filePath ? 'protected' : 'url', filePath: filePath || '' };
}

function validateHref(value: string): string {
    const href = value.trim();
    const message = 'Enter a complete HTTP or HTTPS URL with a fully qualified domain name, such as https://example.com/file.pdf.';
    if (!/^https?:\/\/[^/?#]+/i.test(href) || /[\s\\\x00-\x1f\x7f]/.test(href)
        || /%(?![a-f\d]{2})/i.test(href)) throw new Error(message);
    let parsed: URL;
    try { parsed = new URL(href); } catch { throw new Error(message); }
    const hostname = parsed.hostname.replace(/\.$/, '');
    const labels = hostname.split('.');
    if (parsed.username || parsed.password || hostname.length > 253 || labels.length < 2
        || labels.some(label => !/^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?$/i.test(label))
        || !/^(?:[a-z]{2,63}|xn--[a-z\d-]+)$/i.test(labels[labels.length - 1])) throw new Error(message);
    return href;
}

export function LinkDialog({ editor, request, api }: { editor: Editor; request: number; api?: Api | null }) {
    const [draft, setDraft] = useState<LinkDraft | null>(null);
    const [error, setError] = useState('');
    const handledRequest = useRef(0);
    const [fileValid, setFileValid] = useState(false);
    const apiRef = useRef(api);
    apiRef.current = api;

    useEffect(() => {
        setDraft(null);
        setError('');
        const dom = editor.view.dom;
        const onLinkEvent = (event: MouseEvent) => {
            // Use the iframe's DOM without instanceof checks against the parent window.
            const target = event.target as Node | null;
            const element = target?.nodeType === 1 ? target as Element : target?.parentElement;
            const anchor = element?.closest('a[href]');
            if (!anchor || !dom.contains(anchor)) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            // ProseMirror invokes link handleClick from its mousedown/mouseup
            // sequence, before the browser click event. Block that sequence too.
            if (event.type !== 'click' || event.button !== 0 || !editor.isEditable) return;
            const next = readLink(editor, editor.view.posAtDOM(anchor, 0), apiRef.current);
            if (next) { setError(''); setFileValid(false); setDraft(next); }
        };
        const events = ['mousedown', 'click', 'auxclick'] as const;
        events.forEach(type => dom.addEventListener(type, onLinkEvent, true));
        return () => events.forEach(type => dom.removeEventListener(type, onLinkEvent, true));
    }, [editor]);

    useEffect(() => {
        if (!request || request === handledRequest.current || editor.isDestroyed) return;
        handledRequest.current = request;
        const next = readLink(editor, undefined, api);
        setFileValid(false);
        setError(next ? '' : 'Select text within a single paragraph or place the cursor there.');
        setDraft(next);
    }, [request, editor, api]);

    const close = () => { setDraft(null); setError(''); setFileValid(false); };
    const save = () => {
        if (!draft || editor.isDestroyed) return;
        try {
            if (draft.source === 'protected' && (!api || !draft.filePath || !fileValid)) {
                throw new Error('Select an available protected file.');
            }
            const href = draft.source === 'protected' ? api!.getProtectedStorageDownloadUrl(draft.filePath) : validateHref(draft.href);
            const attrs = { href, title: draft.title.trim() || null, target: null };
            if (!draft.text.trim()) throw new Error('Enter the link text.');
            const chain = editor.chain().focus().setTextSelection({ from: draft.from, to: draft.to });
            if (draft.text === draft.originalText) {
                chain.setLink(attrs).setTextSelection(draft.to).unsetMark('link').run();
            } else {
                const marks = editor.state.doc.resolve(draft.from).marks()
                    .filter(mark => mark.type.name !== 'link').map(mark => mark.toJSON());
                chain.insertContentAt({ from: draft.from, to: draft.to }, {
                    type: 'text', text: draft.text, marks: [...marks, { type: 'link', attrs }],
                }).unsetMark('link').run();
            }
            close();
        } catch (failure) {
            setError(failure instanceof Error ? failure.message : 'Unable to insert the link.');
        }
    };

    return <Dialog header={draft?.existing ? 'Edit link' : 'Insert link'} visible={!!draft || !!error}
        onHide={close} style={{ width: '28rem', maxWidth: '95vw' }}
        footer={<><Button label="Cancel" text onClick={close} />
            <Button label="Save" onClick={save} disabled={!draft || (draft.source === 'protected' && !fileValid)} /></>}>
        {error && <div role="alert" aria-atomic="true" className="flex align-items-start gap-2 mb-3 p-3 border-round border-1"
            style={{ background: 'var(--red-50, #fff1f2)', color: 'var(--red-900, #881337)',
                borderColor: 'var(--red-500, #ef4444)', borderLeftWidth: '4px' }}>
            <i className="pi pi-exclamation-circle mt-1" aria-hidden="true" />
            <div><strong>Please check the link</strong><div className="mt-1">{error}</div></div>
        </div>}
        {draft && <div className="flex flex-column gap-3">
            <div role="radiogroup" aria-label="Link source" className="flex gap-3">
                <div className="flex align-items-center gap-2"><RadioButton inputId="editor-link-source-url" name="editor-link-source"
                    value="url" checked={draft.source === 'url'} onChange={() => { setError(''); setDraft({ ...draft, source: 'url' }); }} />
                    <label htmlFor="editor-link-source-url">Arbitrary URL</label></div>
                <div className="flex align-items-center gap-2"><RadioButton inputId="editor-link-source-protected" name="editor-link-source"
                    value="protected" checked={draft.source === 'protected'} disabled={!api}
                    onChange={() => { setError(''); setFileValid(false); setDraft({ ...draft, source: 'protected' }); }} />
                    <label htmlFor="editor-link-source-protected">Protected storage</label></div>
            </div>
            {draft.source === 'url' ? <><label htmlFor="editor-link-url">URL</label>
            <InputText id="editor-link-url" autoFocus value={draft.href}
                onChange={event => setDraft({ ...draft, href: event.target.value })} /></>
                : api && <><label htmlFor="editor-protected-file">Protected file</label>
                    <ProtectedFileSelect api={api} value={draft.filePath} onValidityChange={setFileValid}
                        onChange={filePath => { setError(''); setDraft({ ...draft, filePath }); }} /></>}
            <label htmlFor="editor-link-text">Text</label>
            <InputText id="editor-link-text" value={draft.text}
                onChange={event => setDraft({ ...draft, text: event.target.value })} />
            <label htmlFor="editor-link-tooltip">Tooltip (optional)</label>
            <InputText id="editor-link-tooltip" value={draft.title}
                onChange={event => setDraft({ ...draft, title: event.target.value })} />
        </div>}
    </Dialog>;
}
